import { useState } from "react";
import { Power, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WORDS = {
  customer: {
    del: "Delete this customer",
    off: "Switch this customer off",
    empty: "No POs recorded against it",
    holds: (n) => `${n} PO line${n === 1 ? "" : "s"} recorded — they stay readable`,
    typed: "short code",
    gone: "Its workflow, mail wording and PO-reading profile go with it. Nothing else is touched.",
    kept: "It leaves the header picker and its workflow stops being offered. Every PO recorded against it stays readable in history and reports.",
  },
  person: {
    del: "Delete this person",
    off: "Switch this person off",
    empty: "No PO recorded in their name",
    holds: (n) => `${n} PO line${n === 1 ? "" : "s"} in their name — their work stays readable`,
    typed: "email",
    gone: "They can sign in no longer, and nothing of theirs is left behind.",
    kept: "They can sign in no longer, and their name stays on the POs they worked on.",
  },
};

/** One deliberate removal control: it says the outcome before the dialog opens. */
export const RemoveRecord = ({ kind, name, confirmValue, poLines = 0, busy, onDelete,
                              onSwitchOff, testId }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const words = WORDS[kind];
  const deletes = poLines === 0;
  const label = deletes ? words.del : words.off;
  const note = deletes ? words.empty : words.holds(poLines);

  const close = () => {
    setOpen(false);
    setTyped("");
  };

  const go = async () => {
    await (deletes ? onDelete() : onSwitchOff());
    close();
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant="outline"
        size="sm"
        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900 dark:hover:bg-red-950"
        onClick={() => setOpen(true)}
        disabled={busy}
        data-testid={testId}
      >
        {deletes ? <Trash2 className="mr-2 h-4 w-4" /> : <Power className="mr-2 h-4 w-4" />}
        {label}
      </Button>
      <span className="text-xs text-muted-foreground" data-testid={`${testId}-note`}>{note}</span>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>
              {deletes ? `Delete ${name}?` : `Switch ${name} off?`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deletes ? words.gone : words.kept}
          </p>
          {deletes ? (
            <div>
              <Label className="field-label">
                Type the {words.typed} <span className="font-mono text-foreground">{confirmValue}</span> to confirm
              </Label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="h-9 font-mono"
                data-testid={`${testId}-confirm-input`}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid={`${testId}-dialog-count`}>
              {words.holds(poLines)}. It can be brought back from the switched-off list at any time.
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close} data-testid={`${testId}-cancel-button`}>
              Leave it as it is
            </Button>
            <Button
              variant="destructive"
              onClick={go}
              disabled={busy || (deletes && typed.trim().toLowerCase() !== String(confirmValue).toLowerCase())}
              data-testid={`${testId}-confirm-button`}
            >
              {label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemoveRecord;
